import React, { useEffect, useRef, useState } from "react";
import type { CancelTokenSource } from "axios";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { CustomDateCalendarPopUp, CustomIconButton, CustomTooltip, icon } from "../../../../common/components";
import styles from "./DocumentCenterTable.module.css";
import { translateDate } from "../../../../common/providers";
import { updateFile } from "../../slice/document-center-slice";
import { ApiService } from "../../../../api";

interface DocumentAttribute {
	attributeKey: string;
	attributeValue: string;
	attributeType?: string;
	documentId?: number;
}

interface EffectiveDateCellProps {
	row: {
		documentAttributes?: DocumentAttribute[];
		label: string;
		id: number;
	};
}
const ATTRIBUTES = {
	NUMBER: "Numéro",
	DATE: "Date d'effet du texte",
	NATURE: "Nature du texte contractuel",
	TYPE: "Type de texte",
};
function getValidDate(input: string): Date | null {
	const date = new Date(input);
	return Number.isNaN(date.getTime()) ? null : date;
}

const EffectiveDateCell = ({ row }: EffectiveDateCellProps) => {
	const projectId = useSelector(({ context }) => context.project.id);
	const [open, setOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement>(null);
	const cancelTokenRef = useRef<CancelTokenSource | null>(null);
	const dispatch = useDispatch();

	useEffect(() => {
		cancelTokenRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenRef.current);
		};
	}, []);
	const attributeValue = row?.documentAttributes?.find(
		(attr) => attr.attributeKey === "Date d'effet du texte"
	)?.attributeValue;

	const handleEdit = (e: React.MouseEvent) => {
		e.stopPropagation();
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleChange = (newValue: Date | null) => {
		const token = cancelTokenRef.current?.token;
		let tempValue = { label: row.label, documentAttributes: row.documentAttributes };
		const elementExist =
			(tempValue.documentAttributes?.findIndex((attribute) => attribute.attributeKey === ATTRIBUTES.DATE) ??
				-1) >= 0;
		const formatedDate = (newValue && format(newValue, "yyyy-MM-dd HH:mm:ss")?.substring(0, 10)) || "";
		if (elementExist) {
			if (formatedDate === "") {
				tempValue = {
					...tempValue,
					documentAttributes: tempValue.documentAttributes?.filter(
						(attribute) => attribute.attributeKey !== ATTRIBUTES.DATE
					),
				};
			} else {
				tempValue = {
					...tempValue,
					documentAttributes: tempValue.documentAttributes?.map((attribute) =>
						attribute.attributeKey === ATTRIBUTES.DATE
							? { ...attribute, attributeValue: formatedDate }
							: attribute
					),
				};
			}
		} else if (formatedDate !== "") {
			tempValue = {
				...tempValue,
				documentAttributes: [
					...(tempValue.documentAttributes || []),
					{
						attributeKey: ATTRIBUTES.DATE,
						attributeValue: formatedDate,
						documentId: row.id,
						attributeType: "DATE",
					},
				],
			};
		}
		dispatch(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- arg type isn't inferable from the JS thunk
			(updateFile as any)({ projectId, docId: row.id, documentAttributes: tempValue.documentAttributes, token })
		);
		setOpen(false);
	};

	return (
		<CustomTooltip showWhenOverflow title={attributeValue}>
			<div className={styles["innerCell--veolia"]}>
				{translateDate(attributeValue)}
				<CustomIconButton
					ref={anchorRef}
					className={styles.editBtn}
					color={(open && "primary") || "secondary"}
					icon={icon.faPen}
					size="xs"
					variant="outlined"
					onClick={handleEdit}
				/>
				<CustomDateCalendarPopUp
					ref={anchorRef}
					open={open}
					value={getValidDate(attributeValue || "")}
					onChange={handleChange}
					onClose={handleClose}
				/>
			</div>
		</CustomTooltip>
	);
};

export default EffectiveDateCell;
